import { jsxs, jsx } from 'react/jsx-runtime';
import { useResourceDataContextSafe } from '../../../../resource-detail/components/context/resourceDataContext.js';
import { openExternal } from '@fabric-app/ui/components/Link/LinkExternal';
import { styled, css } from 'styled-components';
import { getEmbedResourcePath } from './utils.js';
import { isNumber } from '@fabric-app/utils/guards';
import { mediaNotMobile, mediaHover, cssTextEllipsis } from '@fabric-app/ui/constants/styled-utils';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { Flex } from '@fabric-app/ui/components/Flex';
import Tooltip from '@fabric-app/ui/components/Tooltip';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { PreviewAudioPlayer } from './PreviewAudioPlayer.js';
import { P, SPAN } from '@fabric-app/ui/components/Typography';
import { formatFileSize } from '@fabric-app/utils/number';
import { ButtonArrowOpen } from './ButtonArrowOpen.js';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { useShowExpandedResource } from '../../../../resources/stores/useExpandedResourceStore.js';
import { getResourceKindIcon } from '../../../../resources/constants/resourceIcons.js';
import { isInNativeApp } from '@fabric-app/compat/capabilities/platform';
import { useRouter } from '@fabric-app/compat';

const EmbedSimpleView = ({ title, resourceId, uploadProgress, fileSize, fileUrl, notFound, deleteNode, kind = "default", parentResourceId, extension, hideDropdownMenu = false, containerStyle, dropdownMenuPosition = "outside", isTranscibing = false, mimeType }) => {
  const { resource } = useResourceDataContextSafe();
  const isExternalEmbed = parentResourceId && parentResourceId !== resource.id;
  const showExpandedResource = useShowExpandedResource();
  const router = useRouter();
  const canOpen = resourceId && kind && !notFound;
  const handleOpen = canOpen ? (e) => {
    if (e.target !== e.currentTarget && "tagName" in e.currentTarget && e.currentTarget.tagName === "button") {
      return;
    }
    if ([
      "space",
      "folder"
    ].includes(kind)) {
      const path = getEmbedResourcePath({
        resourceId,
        type: kind
      });
      if (isInNativeApp()) router.push(path);
      else openExternal(path);
    } else {
      showExpandedResource(resourceId, {
        storeCurrentAsPrevious: true,
        disableSwitcher: true
      });
    }
  } : void 0;
  const isUploadProgress = isNumber(uploadProgress);
  const Icon = getResourceKindIcon(kind);
  const isAudio = kind === "audio" && !!fileUrl;
  const isSimpleView = !isAudio;
  return /* @__PURE__ */ jsxs(Container, {
    style: {
      ...containerStyle,
      ...isUploadProgress ? {
        cursor: "progress"
      } : notFound ? {
        cursor: "not-allowed"
      } : void 0
    },
    children: [
      canOpen && /* @__PURE__ */ jsx("a", {
        style: {
          position: "absolute",
          inset: 0,
          zIndex: 0,
          cursor: "pointer"
        },
        onClick: handleOpen
      }),
      /* @__PURE__ */ jsx(IconContainer, {
        children: isTranscibing ? /* @__PURE__ */ jsx(Tooltip, {
          label: "Transcribing...",
          children: /* @__PURE__ */ jsx(Spinner, {
            size: 12,
            thickness: 3
          })
        }) : /* @__PURE__ */ jsx(Icon, {})
      }),
      isAudio && /* @__PURE__ */ jsx(PreviewAudioPlayer, {
        fileUrl: resource.fileUrl || fileUrl,
        mimeType
      }),
      isSimpleView && /* @__PURE__ */ jsxs(TextContainer, {
        children: [
          notFound ? /* @__PURE__ */ jsx(Tooltip, {
            label: "You don't have access to this item",
            children: /* @__PURE__ */ jsxs(SPAN, {
              size: "lg",
              color: "placeholder",
              weight: 500,
              children: [
                title,
                extension && `.${extension}`
              ]
            })
          }) : /* @__PURE__ */ jsxs(Flex, {
            overflow: "hidden",
            alignItems: "center",
            children: [
              /* @__PURE__ */ jsx(Title, {
                as: "span",
                color: isUploadProgress ? "placeholder" : "primary",
                children: title
              }),
              extension && /* @__PURE__ */ jsxs(SPAN, {
                size: "lg",
                color: "tertiary",
                weight: 500,
                children: [
                  ".",
                  extension
                ]
              })
            ]
          }),
          isUploadProgress && /* @__PURE__ */ jsxs(AdditionalTextInfo, {
            children: [
              "—",
              /* @__PURE__ */ jsx(Spinner, {
                size: 12,
                thickness: 3
              }),
              Math.round(uploadProgress),
              "%"
            ]
          }),
          isExternalEmbed && resourceId && kind ? /* @__PURE__ */ jsx(ButtonArrowOpen, {
            resourceId,
            kind,
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpen?.(e);
            }
          }) : isNumber(fileSize) && kind !== "notepad" && /* @__PURE__ */ jsx(AdditionalTextInfo, {
            as: "span",
            children: formatFileSize(fileSize)
          })
        ]
      }),
      !hideDropdownMenu && /* @__PURE__ */ jsx(DropdownContainer, {
        dropdownMenuPosition,
        children: /* @__PURE__ */ jsxs(DropdownMenu.Base, {
          renderTriggerElement: (props) => /* @__PURE__ */ jsx(DropdownButton, {
            ...props,
            onClick: (e) => {
              e.stopPropagation();
            }
          }),
          children: [
            /* @__PURE__ */ jsx(DropdownMenu.Group, {
              children: /* @__PURE__ */ jsx(DropdownMenu.Item, {
                onClick: handleOpen,
                children: "View item"
              })
            }),
            deleteNode && /* @__PURE__ */ jsx(DropdownMenu.Group, {
              children: /* @__PURE__ */ jsx(DropdownMenu.Item, {
                onSelect: deleteNode,
                onClick: (e) => e.stopPropagation(),
                variant: "danger",
                children: "Delete"
              })
            })
          ]
        })
      })
    ]
  });
};
const DropdownContainer = styled.div.withConfig({
  displayName: "PreviewSimple__DropdownContainer",
  componentId: "sc-8bc3f884-0"
})([
  ``,
  `{position:absolute;height:100%;`,
  ` top:0;background:`,
  `;border-radius:6px;display:flex;align-items:center;padding-left:6px;padding-right:6px;}`
], mediaNotMobile, ({ dropdownMenuPosition }) => dropdownMenuPosition === "outside" ? css([
  `left:calc(100% - 6px);border-top-left-radius:0;border-bottom-left-radius:0;`
]) : css([
  `right:0;`
]), cssVar["color-bg-section"]);
const DropdownButton = styled(DropdownMenu.TriggerButtonThreeDots).withConfig({
  displayName: "PreviewSimple__DropdownButton",
  componentId: "sc-8bc3f884-1"
})([
  `width:1.375rem;height:1.6875rem;flex-shrink:0;padding:0;border-radius:0;rotate:90deg;border-radius:4px;background:transparent;color:`,
  `;&[data-state='open']{display:flex !important;}`
], cssVar["color-text-placeholder"]);
const Container = styled(Flex).attrs(() => {
  return {
    alignItems: "center"
  };
}).withConfig({
  displayName: "PreviewSimple__Container",
  componentId: "sc-8bc3f884-2"
})([
  `margin-left:0.1rem;margin-right:0.1rem;position:relative;gap:10px;padding-left:0.375rem;height:1.875rem;padding-right:0.4375rem;text-transform:none;font-weight:500;text-decoration:none !important;border-radius:6px;width:max-content;max-width:100%;background:`,
  `;`,
  `{`,
  `{opacity:0;}}.ProseMirror-selectednode &{&::before{z-index:-1;content:'';position:absolute;top:0;left:0;bottom:0;right:0;border-radius:6px;outline:2px solid rgba(var(--color-primary),0.3);}}`,
  `{&:hover{background:`,
  `;.ProseMirror-selectednode &{&::before{right:-1.75rem;}}`,
  `{opacity:1;}}}`
], cssVar["color-bg-secondary"], mediaNotMobile, DropdownContainer, mediaHover, cssVar["color-bg-section"], DropdownContainer);
const IconContainer = styled(Flex).attrs({
  alignItems: "center",
  justifyContent: "center"
}).withConfig({
  displayName: "PreviewSimple__IconContainer",
  componentId: "sc-8bc3f884-3"
})([
  `height:1rem;width:1rem;color:`,
  `;`
], cssVar["color-text-primary"]);
const TextContainer = styled(Flex).attrs({
  alignItems: "baseline",
  justifyContent: "center"
}).withConfig({
  displayName: "PreviewSimple__TextContainer",
  componentId: "sc-8bc3f884-4"
})([
  ``,
  `;gap:6px;`
], cssTextEllipsis);
const AdditionalTextInfo = styled(P).attrs((props) => {
  return {
    color: "placeholder",
    size: "xxs",
    weight: 500,
    ...props
  };
}).withConfig({
  displayName: "PreviewSimple__AdditionalTextInfo",
  componentId: "sc-8bc3f884-5"
})([
  `display:flex;align-items:center;gap:4px;white-space:nowrap;`
]);
const Title = styled(P).attrs({
  size: "lg",
  weight: 500,
  ellipsis: true
}).withConfig({
  displayName: "PreviewSimple__Title",
  componentId: "sc-8bc3f884-6"
})([
  ``
]);

export { EmbedSimpleView };
//# sourceMappingURL=PreviewSimple.js.map
